import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Button, CustomSelect, RichTextEditor, SaveDraftModal, Toggle } from '@/components/ui';
import { Heading, Label, Paragraph } from '@/components/ui/Typography';
import Input from '@/components/ui/Input/Input';
import TextArea from '@/components/ui/TextArea/TextArea';
import FileUpload from '@/components/ui/FileUpload';
import { usePressRelease, useCreatePressRelease, useUpdatePressRelease, usePressReleaseCategories, useArticleTags } from '@/lib/api/hooks/press-release';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import { PressReleaseFormSchema, type PressReleaseCategory } from '@/lib/api/types';
import { showToast } from '@/lib/toast';
import { z } from 'zod';
import { ArrowLeft, FilesIcon, ImagesIcon } from 'lucide-react';
import languageOptions from '@/utils/constants/languageOptions';

type PressReleaseFormInput = z.input<typeof PressReleaseFormSchema>;
type PressReleaseFormOutput = z.output<typeof PressReleaseFormSchema>;

const DEFAULT_FORM_VALUES: PressReleaseFormInput = {
  title: '',
  subtitle: '',
  summary: '',
  language: '',
  category: '',
  readTimeMinutes: '',
  content: '',
  coverImageUrl: '',
  coverImageTitle: '',
  additionalImageUrls: [],
  additionalFileUrls: [],
  articleTag: '',
  landingPage: false,
  pressRelease: false,
  pressReleaseBanner: false,
  allowComment: true,
};

const mapPressReleaseToFormValues = (article?: Record<string, unknown>): PressReleaseFormInput => {
  if (!article) {
    return DEFAULT_FORM_VALUES;
  }

  const tagValue = Array.isArray(article.articleTag)
    ? (article.articleTag as string[]).join(', ')
    : String(article.articleTag || '');

  return {
    ...DEFAULT_FORM_VALUES,
    title: String(article.title || ''),
    subtitle: String(article.subtitle || ''),
    summary: String(article.summary || ''),
    language: String(article.language || ''),
    category: String(article.category || ''),
    readTimeMinutes: String(article.readTimeMinutes || '2'),
    content: String(article.content || ''),
    coverImageUrl: String(article.coverImageUrl || article.coverImage || ''),
    coverImageTitle: String(article.coverImageTitle || ''),
    additionalImageUrls: Array.isArray(article.additionalImageUrls)
      ? (article.additionalImageUrls as string[])
      : [],
    additionalFileUrls: Array.isArray(article.additionalFileUrls)
      ? (article.additionalFileUrls as string[])
      : [],
    articleTag: tagValue,
    landingPage: Boolean(article.landingPage),
    pressRelease: article.pressRelease !== undefined ? Boolean(article.pressRelease) : true,
    pressReleaseBanner: Boolean(article.pressReleaseBanner),
    allowComment: article.allowComment !== undefined ? Boolean(article.allowComment) : true,
  };
};

const FALLBACK_CATEGORY_OPTIONS = [
  { value: 'GENERAL_UPDATES', label: 'General Updates' }
];

const parseTagString = (value: string): string[] => value
  .split(',')
  .map((tag) => tag.trim())
  .filter(Boolean);

const getFileNameFromUrl = (url: string): string => {
  const cleaned = url.split('?')[0].split('#')[0];
  const name = cleaned.split('/').pop();
  return name && name.trim() ? decodeURIComponent(name) : 'uploaded-file';
};

const getLocalFileKey = (file: File): string => `${file.name}-${file.size}-${file.lastModified}`;

const PressReleaseEditor: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine if this is edit mode (has id param and path includes /edit) or new mode (path ends with /new)
  const isEditMode = !!id && location.pathname.includes('/edit');
  const articleId = isEditMode ? id : "";

  const { data: pressReleaseData, isLoading: loadingPressRelease } = usePressRelease(
    articleId || '',

  );
  const { data: categoryResponse, isLoading: loadingCategories } = usePressReleaseCategories();
  const { data: articleTagsResponse, isLoading: loadingArticleTags } = useArticleTags();

  const { mutate: createPressRelease, isPending: isCreating } = useCreatePressRelease();
  const { mutate: updatePressRelease, isPending: isUpdating } = useUpdatePressRelease();
  const fileUploadMutation = useFileUpload();
  const fileUploadRef = useRef<HTMLDivElement>(null);

  const [localCoverFile, setLocalCoverFile] = useState<File | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string>('');
  const [existingCoverUrl, setExistingCoverUrl] = useState<string>('');
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);
  const [additionalFileFiles, setAdditionalFileFiles] = useState<File[]>([]);
  const [additionalImageUploadKey, setAdditionalImageUploadKey] = useState(0);
  const [additionalFileUploadKey, setAdditionalFileUploadKey] = useState(0);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
const [isUploadingFile, setIsUploadingFile] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<PressReleaseFormInput, unknown, PressReleaseFormOutput>({
    resolver: zodResolver(PressReleaseFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const isSaving = isCreating || isUpdating;
  const isBusy = isSaving || isUploadingFile;

  useEffect(() => {
    const shouldHydrateEditForm = isEditMode && pressReleaseData?.data;
    if (shouldHydrateEditForm) {
      const nextValues = mapPressReleaseToFormValues(pressReleaseData.data as Record<string, unknown>);
      reset(nextValues);
      setExistingCoverUrl(nextValues.coverImageUrl);
    } else if (!isEditMode) {
      reset(DEFAULT_FORM_VALUES);
      setExistingCoverUrl('');
    }

    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setLocalCoverFile(null);
    setAdditionalImageFiles([]);
    setAdditionalFileFiles([]);
    setAdditionalImageUploadKey((prev) => prev + 1);
    setAdditionalFileUploadKey((prev) => prev + 1);
  }, [isEditMode, pressReleaseData, reset]);

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
    };
  }, [localPreviewUrl]);

  const handleBrowseFile = useCallback(() => {
    fileUploadRef.current?.click();
  }, []);

  const handleLocalRemove = useCallback(() => {
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setLocalCoverFile(null);
    setValue('coverImageUrl', existingCoverUrl || '', { shouldValidate: true });
  }, [existingCoverUrl, setValue]);

  const handleCoverFileChange = useCallback((files: File[] | null) => {
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    const objectUrl = URL.createObjectURL(file);
    setLocalPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return objectUrl;
    });

    setLocalCoverFile(file);
    setValue('coverImageUrl', objectUrl, { shouldValidate: true });
    if (!watch('coverImageTitle')) {
      setValue('coverImageTitle', file.name);
    }
  }, [setValue, watch]);

  const handleAdditionalImagesChange = useCallback((files: File[] | null) => {
    if (!files || files.length === 0) return;

    setAdditionalImageFiles((prev) => {
      const merged = [...prev, ...files];
      const uniqueByKey = new Map(merged.map((file) => [getLocalFileKey(file), file]));
      return Array.from(uniqueByKey.values());
    });
    setAdditionalImageUploadKey((prev) => prev + 1);
  }, []);

  const handleAdditionalFilesChange = useCallback((files: File[] | null) => {
    if (!files || files.length === 0) return;

    setAdditionalFileFiles((prev) => {
      const merged = [...prev, ...files];
      const uniqueByKey = new Map(merged.map((file) => [getLocalFileKey(file), file]));
      return Array.from(uniqueByKey.values());
    });
    setAdditionalFileUploadKey((prev) => prev + 1);
  }, []);

  const removeAdditionalImageUrl = useCallback((url: string) => {
    const current = watch('additionalImageUrls') || [];
    setValue('additionalImageUrls', current.filter((value) => value !== url), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [setValue, watch]);

  const removeAdditionalFileUrl = useCallback((url: string) => {
    const current = watch('additionalFileUrls') || [];
    setValue('additionalFileUrls', current.filter((value) => value !== url), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [setValue, watch]);

  const removePendingAdditionalImage = useCallback((fileKey: string) => {
    setAdditionalImageFiles((prev) => prev.filter((file) => getLocalFileKey(file) !== fileKey));
  }, []);

  const removePendingAdditionalFile = useCallback((fileKey: string) => {
    setAdditionalFileFiles((prev) => prev.filter((file) => getLocalFileKey(file) !== fileKey));
  }, []);

  const uploadAdditionalFiles = useCallback(
    async (files: File[], category: 'IMAGE' | 'DOCUMENT', onFileUploaded?: (file: File) => void) => {
      const uploadedUrls: string[] = [];

      for (const file of files) {
        const uploadResult = await fileUploadMutation.mutateAsync({ file, category });
        if (!uploadResult.success || !uploadResult.data?.fileUrl) {
          throw new Error(`Failed to upload ${file.name}`);
        }
        uploadedUrls.push(uploadResult.data.fileUrl);
        onFileUploaded?.(file);
      }

      return uploadedUrls;
    },
    [fileUploadMutation]
  );

  const submitWithStatus = async (data: PressReleaseFormOutput, saveStatus: 'DRAFT' | 'PUBLISH') => {
    let finalCoverImageUrl = data.coverImageUrl;

    if (localCoverFile) {
      try {
        setIsUploadingFile(true);
        const uploadResult = await fileUploadMutation.mutateAsync({ file: localCoverFile, category: 'IMAGE' });
        finalCoverImageUrl = uploadResult?.data?.fileUrl || '';
      } catch (error) {
        console.error(error);
        showToast.error('Cover image upload failed. Please try again.');
        return;
      } finally {
        setIsUploadingFile(false);
      }
    }

    if (!finalCoverImageUrl) {
      showToast.error('Please upload a cover image');
      return;
    }

    let finalAdditionalImageUrls = [...(data.additionalImageUrls || [])];
    let finalAdditionalFileUrls = [...(data.additionalFileUrls || [])];

    if (additionalImageFiles.length > 0 || additionalFileFiles.length > 0) {
      try {
        setIsUploadingFile(true);
        const uploadedImages = additionalImageFiles.length
          ? await uploadAdditionalFiles(additionalImageFiles, 'IMAGE', (file) => {
              const key = getLocalFileKey(file);
              setAdditionalImageFiles((prev) => prev.filter((item) => getLocalFileKey(item) !== key));
            })
          : [];
        const uploadedFiles = additionalFileFiles.length
          ? await uploadAdditionalFiles(additionalFileFiles, 'DOCUMENT', (file) => {
              const key = getLocalFileKey(file);
              setAdditionalFileFiles((prev) => prev.filter((item) => getLocalFileKey(item) !== key));
            })
          : [];

        finalAdditionalImageUrls = Array.from(new Set([...finalAdditionalImageUrls, ...uploadedImages]));
        finalAdditionalFileUrls = Array.from(new Set([...finalAdditionalFileUrls, ...uploadedFiles]));
        setValue('additionalImageUrls', finalAdditionalImageUrls, { shouldDirty: true, shouldValidate: true });
        setValue('additionalFileUrls', finalAdditionalFileUrls, { shouldDirty: true, shouldValidate: true });
      } catch (error) {
        console.error(error);
        showToast.error('Additional files upload failed. Please try again.');
        return;
      } finally {
        setIsUploadingFile(false);
      }
    }

    const tagsArray = data.articleTag
      .split(',')
      .map((tag: string) => tag.trim())
      .filter(Boolean);

    const payload = {
      title: data.title,
      subtitle: data.subtitle,
      summary: data.summary,
      language: data.language,
      coverImageUrl: finalCoverImageUrl,
      coverImageTitle: data.coverImageTitle || '',
      additionalImageUrls: finalAdditionalImageUrls,
      additionalFileUrls: finalAdditionalFileUrls,
      articleTag: tagsArray,
      category: data.category,
      readTimeMinutes: Number.parseInt(data.readTimeMinutes, 10),
      landingPage: data.landingPage,
      pressRelease: data.pressRelease,
      pressReleaseBanner: data.pressReleaseBanner,
      allowComment: data.allowComment,
      content: data.content,
      status: saveStatus,
    };

    if (isEditMode) {
      updatePressRelease(
        { pressReleaseId: articleId as string, data: payload },
        {
          onSuccess: () => {
            if (saveStatus === 'PUBLISH') {
              showToast.publishSuccess({
                title: 'Publish Successful',
                message: 'You have successfully published your press release',
                onGoHome: () => {
                  navigate('/admin/press-release');
                }
              });
            } else {
              showToast.success('Press release updated successfully!');
              navigate('/admin/press-release');
            }
          },
          onError: (error) => {
            showToast.error('Failed to update: ' + (error.message || 'Unknown error'));
          }
        }
      );
    } else {
      createPressRelease(payload, {
        onSuccess: () => {
          if (saveStatus === 'PUBLISH') {
            showToast.publishSuccess({
              title: 'Publish Successful',
              message: 'You have successfully published your press release',
              onGoHome: () => {

                navigate('/admin/press-release');
              }
            });
          } else {
            showToast.success('Press release created as draft!');
            navigate('/admin/press-release');
          }
        },
        onError: (error) => {
          showToast.error('Failed to create: ' + (error.message || 'Unknown error'));
        }
      });
    }
  };

  const displayCoverImageUrl = localPreviewUrl || existingCoverUrl;
  const additionalImageUrls = watch('additionalImageUrls') || [];
  const additionalFileUrls = watch('additionalFileUrls') || [];

  const categoryOptions = useMemo(() => {
    const categories = categoryResponse?.data ?? [];
    if (!categories.length) {
      return FALLBACK_CATEGORY_OPTIONS;
    }

    return categories.map((cat: PressReleaseCategory) => ({
      value: cat.value,
      label: cat.value
        .split('_')
        .map((part: string) => part.charAt(0) + part.slice(1).toLowerCase())
        .join(' '),
    }));
  }, [categoryResponse]);

  const articleTagOptions = useMemo(() => {
    const tags = articleTagsResponse?.data ?? [];
    return tags.map((tag: {displayName: string; value: string}) => ({ value: tag.value, label: tag.displayName }));
  }, [articleTagsResponse?.data]);

  const handleBackClick = useCallback(() => {
    if (isDirty || Boolean(localCoverFile) || additionalImageFiles.length > 0 || additionalFileFiles.length > 0) {
      setShowUnsavedModal(true);
      return;
    }
    navigate('/admin/press-release');
  }, [
    isDirty,
    localCoverFile,
    additionalImageFiles.length,
    additionalFileFiles.length,
    navigate,
  ]);

  return (
    <div className="space-y-5 p-6 relative">
      <div className="flex items-center justify-between sticky top-0 z-10 bg-white">
        <Button
          type="button"
          variant="ghost"
          onClick={handleBackClick}
          className="!px-0 !py-0 text-[#047857]  font-semibold hover:underline flex items-center gap-2"
        >
          <ArrowLeft height={16} width={24} /> Back to Press Release
        </Button>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            loading={isBusy}
            onClick={handleSubmit((data) => submitWithStatus(data, 'DRAFT'))}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            loading={isBusy}
            onClick={handleSubmit((data) => submitWithStatus(data, 'PUBLISH'))}
          >
            Publish
          </Button>
        </div>
      </div>

<div className="mx-auto max-w-5xl space-y-6">

{/* Title and Breadcrumb */}
        <div>
          <Heading level={3} className="mb-3 leading-[1.1] font-semibold text-[#0F172A]">
            {isEditMode ? 'Edit Press Release' : 'Create Press Release'}
          </Heading>
          <nav className='flex items-center gap-2 text-sm'>
            <button
              type="button"
              onClick={() => navigate('/admin/press-release')}
              disabled={isSaving}
              className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
            >
              All Press Releases
            </button>
            <span className='text-gray-400'>/</span>
            <span className='text-gray-600'>{isEditMode ? 'Edit' : 'New Press Release'}</span>
          </nav>
        </div>

      {loadingPressRelease && isEditMode ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#047857]"></div>
            <Paragraph className="mt-4 text-sm text-gray-500">Loading press release...</Paragraph>
          </div>
        </div>
      ) : (
        <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">1. Basic Information</Heading>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-8">
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Title"
                      required
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.title?.message ? String(errors.title.message) : undefined}
                      $marginbottom="0"
                      placeholder="Enter title"
                    />
                  )}
                />

                <Controller
                  name="subtitle"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Subtitle"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.subtitle?.message ? String(errors.subtitle.message) : undefined}
                      $marginbottom="0"
                      placeholder="Enter subtitle"
                    />
                  )}
                />
              </div>

              <Controller
                name="summary"
                control={control}
                render={({ field }) => (
                  <TextArea
                    label="Excerpt / Summary"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.summary?.message ? String(errors.summary.message) : undefined}
                    maxLength={300}
                    $marginbottom="0"
                    textAreaClassName="bg-[var(--inputBgColor)] min-h-[8rem] text-text-primary"
                    placeholder="Enter a concise summary"
                  />
                )}
              />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5">
      <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">
  2. Cover Image
  <span aria-hidden="true" className="ml-1 text-red-500">*</span>
  <span className="sr-only">(required)</span>
</Heading>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-gray-200 overflow-hidden bg-gray-50 max-h-[220px]">
                {displayCoverImageUrl ? (
                  <img src={displayCoverImageUrl} alt="Cover" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full min-h-[220px] flex items-center justify-center text-sm text-gray-500">
                    <Paragraph className="text-sm text-gray-500">No image selected</Paragraph>
                  </div>
                )}
              </div>

              <div>
                <FileUpload
                  ref={fileUploadRef}
                  label=""
                  placeholder=""
                  $maxSizeMB={10}
                  multiple={false}
                  showPreview={false}
                  allowedFileTypes={['image/*']}
                  onChange={handleCoverFileChange}
                  error={errors.coverImageUrl?.message ? String(errors.coverImageUrl.message) : undefined}
                  containerClassName="border border-dashed border-gray-300 min-h-[220px]"
                >
                  <div className="flex h-full min-h-[180px] flex-col items-center justify-center gap-2 text-center">
                    <Paragraph className="text-sm font-semibold text-[#334155]">Upload cover image</Paragraph>
                    <Paragraph className="text-xs">Drag and drop or click to browse</Paragraph>
                    <Paragraph className="text-xs ">Recommended: (JPG, PNG, WEBP)</Paragraph>
                     <Paragraph className="text-xs ">Max file size: 10MB</Paragraph>
                  </div>
                </FileUpload>
                <div className="mt-4 flex items-center gap-3">
                  <Button type="button" variant="secondary" onClick={handleBrowseFile}>
                    Select Image
                  </Button>
                  {localCoverFile && (
                    <Button type="button" variant="danger" onClick={handleLocalRemove}>
                      Remove
                    </Button>
                  )}
                 
                </div>
              </div>
            </div>
          </section>
       <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">
  3. Article Body
  <span aria-hidden="true" className="ml-1 text-red-500">*</span>
  <span className="sr-only">(required)</span>
</Heading>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <div>
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Write press release content..."
                    error={errors.content?.message ? String(errors.content.message) : undefined}
                    required
                  />
              
                </div>
              )}
            />
          </section>
          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">4. Metadata</Heading>
            <div className="grid items-end grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <div>
                    <CustomSelect
                    label="Category"
                      value={field.value}
                      onChange={field.onChange}
                      required
                      options={categoryOptions}
                      placeholder={loadingCategories ? 'Loading categories...' : 'Select Category'}
                      disabled={loadingCategories}
                      error={errors.category?.message ? String(errors.category.message) : undefined}
                    />
                 
                  </div>
                )}
              />

              <Controller
                name="language"
                control={control}
                render={({ field }) => (
                  <div>
                    <CustomSelect label="Language" value={field.value} onChange={field.onChange} required options={languageOptions} error={errors.language?.message ? String(errors.language.message) : undefined} />
                 
                  </div>
                )}
              />

              <Controller
                name="articleTag"
                control={control}
                render={({ field }) => (
                  <div>
                    <CustomSelect
                    label="Tags"
                      value={parseTagString(field.value || '')}
                      onChange={(selectedValues) => {
                        const values = Array.isArray(selectedValues) ? selectedValues : [];
                        field.onChange(values.join(', '));
                      }}
                      options={articleTagOptions}
                      placeholder={loadingArticleTags ? 'Loading tags...' : 'Select or create tags'}
                      disabled={loadingArticleTags}
                      isMulti
                      isSearchable
                      isClearable
                      isCreatable
                      multiValueLayout='scroll'
                    />
                  </div>
                )}
              />

              <Controller
                name="readTimeMinutes"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Read Time (minutes)"
                    required
                    type="number"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.readTimeMinutes?.message ? String(errors.readTimeMinutes.message) : undefined}
                    $marginbottom="0"
                    placeholder="e.g. 2"
                  />
                )}
              />
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5">
            <Heading level={4} className="mb-4 text-lg font-semibold text-gray-900">5. Additional Assets</Heading>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label className="mb-2 block text-sm font-medium text-gray-700">Additional Images</Label>
                <FileUpload
                  key={additionalImageUploadKey}
                  onChange={handleAdditionalImagesChange}
                  disabled={isBusy}
                  allowedFileTypes={['image/*']}
                  multiple
                  showPreview={false}
                  $maxSizeMB={10}
                  placeholder=""
                  containerClassName="min-h-32 flex flex-col items-center justify-center gap-2 text-center bg-gray-50 border-gray-200"
                >
                  <ImagesIcon className="mx-auto" size={48} strokeWidth={1.25} />
                  <Paragraph>Drag images to upload</Paragraph>
                  <Paragraph className="!text-sm ">PNG, JPG, JPEG, WEBP. Max 10MB each.</Paragraph>
                </FileUpload>

                {additionalImageUrls.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {additionalImageUrls.map((url) => (
                      <div key={url} className="inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5">
                        <span className="truncate text-sm text-gray-700" title={url}>{getFileNameFromUrl(url)}</span>
                        <button
                          type="button"
                          className="shrink-0 rounded-full border border-[#E6BFC1] px-2 py-0.5 text-xs font-semibold text-[#A8393D] hover:bg-[#FFF5F6]"
                          onClick={() => removeAdditionalImageUrl(url)}
                          aria-label="Remove additional image"
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {additionalImageFiles.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {additionalImageFiles.map((file) => {
                      const key = getLocalFileKey(file);
                      return (
                        <div key={key} className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#9BC3AB] bg-[#F3F9F5] px-3 py-1.5">
                          <span className="truncate text-sm text-gray-700" title={file.name}>{file.name}</span>
                          <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#0D6B3C]">Pending</span>
                          <button
                            type="button"
                            className="shrink-0 rounded-full border border-[#E6BFC1] px-2 py-0.5 text-xs font-semibold text-[#A8393D] hover:bg-[#FFF5F6]"
                            onClick={() => removePendingAdditionalImage(key)}
                            aria-label="Remove pending additional image"
                          >
                            x
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <Label className="mb-2 block text-sm font-medium text-gray-700">Additional Files</Label>
                <FileUpload
                  key={additionalFileUploadKey}
                  onChange={handleAdditionalFilesChange}
                  disabled={isBusy}
                  multiple
                  showPreview={false}
                  $maxSizeMB={50}
                  placeholder=""
                  containerClassName="min-h-32 flex flex-col items-center justify-center gap-2 text-center bg-gray-50 border-gray-200"
                >
                  <FilesIcon className="mx-auto" size={48} strokeWidth={1.25} />
                  <Paragraph>Drag documents to upload</Paragraph>
                  <Paragraph className="!text-sm ">PDF, DOCX, XLSX, PPTX. Max 50MB each.</Paragraph>
                </FileUpload>

                {additionalFileUrls.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {additionalFileUrls.map((url) => (
                      <div key={url} className="inline-flex max-w-full items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5">
                        <span className="truncate text-sm text-gray-700" title={url}>{getFileNameFromUrl(url)}</span>
                        <button
                          type="button"
                          className="shrink-0 rounded-full border border-[#E6BFC1] px-2 py-0.5 text-xs font-semibold text-[#A8393D] hover:bg-[#FFF5F6]"
                          onClick={() => removeAdditionalFileUrl(url)}
                          aria-label="Remove additional file"
                        >
                          x
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {additionalFileFiles.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {additionalFileFiles.map((file) => {
                      const key = getLocalFileKey(file);
                      return (
                        <div key={key} className="inline-flex max-w-full items-center gap-2 rounded-full border border-[#9BC3AB] bg-[#F3F9F5] px-3 py-1.5">
                          <span className="truncate text-sm text-gray-700" title={file.name}>{file.name}</span>
                          <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] font-semibold text-[#0D6B3C]">Pending</span>
                          <button
                            type="button"
                            className="shrink-0 rounded-full border border-[#E6BFC1] px-2 py-0.5 text-xs font-semibold text-[#A8393D] hover:bg-[#FFF5F6]"
                            onClick={() => removePendingAdditionalFile(key)}
                            aria-label="Remove pending additional file"
                          >
                            x
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-300 px-4 py-6">
            <Controller
              name="allowComment"
              control={control}
              render={({ field }) => (
                <Toggle
                  checked={Boolean(field.value)}
                  onChange={field.onChange}
                  label="Allow comments?"
                  description="This disables/enables the comment section of this content"
                />
              )}
            />
          </section>

          <section className="rounded-xl border border-gray-300 px-4 py-6 flex flex-col gap-4">
  <Controller
    name="landingPage"
    control={control}
    render={({ field }) => (
      <Toggle
        checked={Boolean(field.value)}
        onChange={field.onChange}
        label="Homepage Hero Highlight"
        description="Show this item in the homepage hero highlight area. The most recently selected item will appear first. "
      />
    )}
  />
  <Controller
    name="pressRelease"
    control={control}
    render={({ field }) => (
      <Toggle
        checked={Boolean(field.value)}
        onChange={field.onChange}
        label="Homepage Press Release Section"
        description="Feature this press release in the press release section on the homepage. The latest selected press release will be shown as the main feature. "
      />
    )}
  />
  <Controller
    name="pressReleaseBanner"
    control={control}
    render={({ field }) => (
      <Toggle
        checked={Boolean(field.value)}
        onChange={field.onChange}
        label="Press Release Page Feature Banner "
        description="Show this press release as the featured banner on the main Press Release page. "
      />
    )}
  />
</section>

   
        </form>
      )}
</div>
      <SaveDraftModal
        isOpen={showUnsavedModal}
        onClose={() => setShowUnsavedModal(false)}
        onConfirm={() => {
          setShowUnsavedModal(false);
          handleSubmit((data) => submitWithStatus(data, 'DRAFT'))();
        }}
        onDiscard={() => {
          setShowUnsavedModal(false);
          navigate('/admin/press-release');
        }}
        title="Save Post?"
        subtitle="You have unsaved changes. Save as draft before leaving?"
        confirmButtonText="Save as Draft"
        discardButtonText="Discard"
      />
    </div>
  );
};

export default PressReleaseEditor;
